import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { CtaBlock } from "@/components/cta";
import { Reveal } from "@/lib/motion";
import { servicioBySlug } from "@/content/services";
import { momentoBySlug } from "@/content/moments";

export default function ServicioDetalle() {
  const { slug } = useParams();
  const servicio = servicioBySlug(slug);

  if (!servicio) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-8">
        <p className="eyebrow mb-4">Servicio no encontrado</p>
        <h1 className="font-serif text-4xl text-ink-deep">Ese servicio no existe en nuestro catálogo</h1>
        <Link to="/servicios" data-testid="servicio-volver-index" className="mt-6 inline-flex text-azul underline underline-offset-4">
          Ver los siete servicios
        </Link>
      </div>
    );
  }

  const momentos = servicio.momentosRelacionados
    .map((m) => momentoBySlug(m))
    .filter(Boolean) as NonNullable<ReturnType<typeof momentoBySlug>>[];

  return (
    <>
      <Seo title={servicio.seoTitle} description={servicio.seoDesc} path={`/servicios/${servicio.slug}`} />
      <PageHero eyebrow={`Servicio · ${String(0).padStart(2, "0")}`} titulo={servicio.nombre} lead={servicio.intro} />

      <section className="bg-parchment">
        <div className="mx-auto grid max-w-7xl gap-16 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-12">
          <div className="space-y-16 lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">Qué incluye</h2>
              <ul className="mt-8">
                {servicio.incluye.map((item, i) => (
                  <li key={i} data-testid={`servicio-incluye-${i}`} className="flex items-start gap-4 border-t border-hairline py-4 last:border-b">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-azul" strokeWidth={2.2} />
                    <span className="text-ink-text">{item}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">Cómo funciona</h2>
              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                {servicio.comoFunciona.map((paso, i) => (
                  <div key={i} className="border-l-2 border-azul/60 pl-5" data-testid={`servicio-paso-${i}`}>
                    <p className="font-mono text-xs tracking-[0.22em] text-azul">{String(i + 1).padStart(2, "0")}</p>
                    <p className="mt-2 leading-relaxed text-ink-text">{paso}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <aside className="space-y-6 lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="border border-hairline bg-parchment-surface/60 p-8" data-testid="servicio-para-quien">
                <p className="eyebrow mb-6">Es para ti si…</p>
                <ul className="space-y-3.5">
                  {servicio.paraQuien.map((p, i) => (
                    <li key={i} className="flex gap-3 text-sm leading-relaxed text-ink-text">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 border border-hairline p-8" data-testid="servicio-momentos">
                <p className="eyebrow mb-6">Momentos donde se vive</p>
                <div className="flex flex-wrap gap-2">
                  {momentos.map((m) => (
                    <Link
                      key={m.slug}
                      to={`/momentos/${m.slug}`}
                      data-testid={`servicio-momento-chip-${m.slug}`}
                      className="border border-hairline bg-white px-4 py-2 text-sm text-ink-text transition-colors hover:border-azul/50 hover:text-azul"
                    >
                      {m.nombre}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-6 border-l-2 border-azul bg-azul-light/40 p-6">
                <p className="text-sm leading-relaxed text-ink-text">
                  Antes de empezar cualquier trabajo sabrás qué incluye, cuánto cuesta y hasta dónde
                  llega. Si tu asunto no es de esta área, te decimos a dónde ir.
                </p>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <CtaBlock
        titulo={`¿Tu asunto es de ${servicio.nombre.toLowerCase()}?`}
        sub="Cuéntanos tu situación y te confirmamos si esta es el área correcta — o cuál sí lo es."
      />
    </>
  );
}
